import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL, ListingApiService } from '@cnt-workspace/data-access';
import { IDtoListingMedia, IListing, IListingWhere } from '@curbnturf/entities';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

const API_URL = BASE_API_URL + 'listing';

export interface IAdminListingPage {
  total: number;
  page: number;
  listings: IListing[];
}

/**
 * The listing calls admin needs that cnt-landing's ListingApiService leaves
 * out. Everything else (section updates, enable/disable, images, sites) is
 * called on ListingApiService directly — the server's ACL lets admins act on
 * any owner's listing (acl.service verifyListingOwner → checkIfAdmin).
 */
@Injectable({ providedIn: 'root' })
export class AdminListingApiService {
  private readonly http = inject(HttpClient);
  private readonly listings = inject(ListingApiService);

  list(where: IListingWhere, page: number, pageSize: number, sort = 'id'): Observable<IAdminListingPage> {
    return this.listings.fetchAll(where, sort, page, pageSize);
  }

  /**
   * Every listing matching `where`, paging through `GET /listing` until
   * `total` is reached. (The legacy weekly report called fetchAll once with
   * the default pageSize of 100, so its counts were wrong past 100 listings.)
   */
  listAll(where: IListingWhere, pageSize = 500): Observable<IListing[]> {
    // Page numbers are tracked here rather than read back from the response:
    // the server echoes the query value, which can arrive as a string.
    const page$ = (page: number) => this.list(where, page, pageSize).pipe(map((res) => ({ page, res })));
    return page$(1).pipe(
      expand(({ page, res }) => (page * pageSize < Number(res.total) && res.listings.length ? page$(page + 1) : EMPTY)),
      map(({ res }) => res.listings),
      reduce((all, listings) => all.concat(listings), [] as IListing[]),
    );
  }

  restore(listingId: number): Observable<IListing> {
    return this.http.post<IListing>(`${API_URL}/${listingId}/restore`, null);
  }

  /** `PUT /listing/:id/featured` — behind the server's AdminAuthGuard. */
  setFeatured(listingId: number, featured: boolean): Observable<IListing> {
    return this.http.put<IListing>(`${API_URL}/${listingId}/featured`, { featured });
  }

  /** `PUT /listing/:id/media` — youtubeCode + rvcastUrl. */
  updateMedia(listingId: number, media: IDtoListingMedia): Observable<IListing> {
    return this.http.put<IListing>(`${API_URL}/${listingId}/media`, media);
  }
}
